import { LocaleEnum } from './language.types';
import { PayMeansCurrencyEnum } from './paymeans.types';

export enum Brand {
  VODAFONE,
  ZIGGO,
}

export enum OneAppInvoiceType {
  REGULAR,
  DEBIT,
  CREDIT,
  SPECIAL,
}

export enum OneAppInvoicePaymentType {
  DIRECT_DEBIT,
  OTHER,
  UNKNOWN,
}

export enum OneAppInvoiceStatus {
  OPEN = 'OPEN',
  PENDING = 'PENDING',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  UNKNOWN = 'UNKNOWN',
}

export type RetrieveChargesSummaryDetails = {
  description: string;
  quantity: number;
  totalAmount: number;
  amountWithoutVat: number;
  vat: number;
  vatRate: number;
  discount: string;
  startDate: string;
  endDate: string;
};

export type RetrieveChargesSummary = {
  totalAmount: number;
  amountWithoutVat: number;
  vat: number;
  retrieveChargesDetails: Array<RetrieveChargesSummaryDetails>;
};

export type ChargesSummaryDynamic = {
  code?: number;
  description: string;
  totalAmount: number;
  amountWithoutVat: number;
  vat: number;
  retrieveChargesDetails: Array<RetrieveChargesSummaryDetails>;
};

type SummaryCharge = {
  orderNumber?: number;
  artikelOrdering?: number;
  artikel3TM?: number;
  definition?: number;
  amount?: number;
  totalBeforeTax?: number;
  taxRate?: number;
};

type SiteCharge = {
  orderNumber?: number;
  artikelOrdering?: number;
  artikel3TM?: number;
  definition?: number;
  amount?: number;
  totalBeforeTax?: number;
  taxeRate?: number;
  customerRef?: number;
  serialNumbers?: number;
  deliveryNumber?: number;
  deliveryDate?: number;
  unitPrice?: number;
  totalPrice?: number;
  discounts?: number;
  netto?: number;
};

type SiteSummary = {
  siteID?: number;
  siteAddress?: number;
  siteTotal?: number;
  amount?: number;
  siteCharges: Array<SiteCharge>;
};

export type GoodsAndServicesData = {
  amount?: number;
  grandTotal: number;
  summaryCharges: Array<SummaryCharge>;
  sitesSummary: Array<SiteSummary>;
};

type ChargeDetail = {
  total: number;
  withoutVat: number;
  vat: number;
};

export type SubscriberSummaryCharges = {
  credit: ChargeDetail;
  discount: ChargeDetail;
  oneTimeCharges: ChargeDetail;
  recurringCharges: ChargeDetail;
  usageCharges: ChargeDetail;
  total: ChargeDetail;
  disclosed: string;
};

export type SubscribersSummaryData = {
  subscriberName: string;
  subscriberId: number;
  description?: number;
  primaryResource: number;
  charges: SubscriberSummaryCharges;
};

export type OneAppInvoiceCharges = {
  recurringCharges: RetrieveChargesSummary;
  usageCharges: RetrieveChargesSummary;
  oneTimeCharges: RetrieveChargesSummary;
  chargesAndTax: RetrieveChargesSummary;
  creditCharges: RetrieveChargesSummary;
  discountCharges: RetrieveChargesSummary;
  purchaseOrderCharges: RetrieveChargesSummary;

  dynamicCharges: Array<ChargesSummaryDynamic>;
  subscribersSummary: Array<SubscribersSummaryData>;
  goodsAndServices: Array<GoodsAndServicesData>;
};

export type OneAppInvoiceItem = {
  brand: Brand;
  id: string;
  type: OneAppInvoiceType;
  creationDate: string | null;
  dueDate: string | null;
  paidDate: string | null;
  dueAmount?: number | null;
  totalAmount: number | null;
  paymentType?: OneAppInvoicePaymentType | null;
  invoiceStatus: OneAppInvoiceStatus;
  showPaymentOption: boolean;
  closeDate: string | null;
  hasChargeDetails: boolean;
};

export type OneAppInvoiceList = {
  regular: OneAppInvoiceItem[];
  nonRegular: OneAppInvoiceItem[];
};

export enum InvoiceSummaryStatusEnum {
  OPEN = 'OPEN',
  NOTPAID = 'NOT_PAID',
  PAYMENTPENDING = 'PAYMENT_PENDING',
  PAID = 'PAID',
}

export enum InvoicePaymentStatusEnum {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export enum InvoicePaymentDetailStatusEnum {
  IDEAL_SUCCESS = 'IDEAL_SUCCESS',
  IDEAL_FAILURE = 'IDEAL_FAILURE',
  IDEAL_CANCELLED = 'IDEAL_CANCELLED',
  IDEAL_EXPIRED = 'IDEAL_EXPIRED',
  IDEAL_OPEN = 'IDEAL_OPEN',
  CREDIT_CARD_SUCCESS = 'CREDIT_CARD_SUCCESS',
  CREDIT_CARD_VESTA_DENIED = 'CREDIT_CARD_VESTA_DENIED',
  CREDIT_CARD_BANK_DENIED = 'CREDIT_CARD_BANK_DENIED',
  FULFILMENT_PERMANENT_FAILURE = 'FULFILMENT_PERMANENT_FAILURE',
  FULFILMENT_TEMPORARY_FAILURE = 'FULFILMENT_TEMPORARY_FAILURE',
}

export enum InvoiceSummaryTypeEnum {
  REGULAR = 'REGULAR',
  CHANGEOFSERVICE = 'CHANGE_OF_SERVICE',
  FIRST = 'FIRST',
  FINAL = 'FINAL',
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
  ADDITIONAL = 'ADDITIONAL',
  SPECIALTY = 'SPECIALTY',
}

export enum InvoicePaymentMethodEnum {
  INGENICO = 'INGENICO',
  IDEAL = 'IDEAL',
  CREDIT_CARD = 'CREDIT_CARD',
}

interface InvoiceSummaryDirectDebit {
  attemptsLeft: number;
  rejectionReason?: string;
}

export interface TokenPaymentResponse {
  token: string;
}

export interface HashPaymentResponse {
  acceptance: string;
  amount: string;
  error: string;
  orderId: string;
  paymentMethod: string;
  shasign: string;
  status: string;
  paymentId: string;
}

export interface InvoiceSummary {
  /** Invoice identification as communicated to customer, referenceable for customer to VZ. */
  id: string;
  status: InvoiceSummaryStatusEnum;
  type: InvoiceSummaryTypeEnum;
  /** On which calendar period the invoice applies to (yyyy-mm). */
  period: string;
  /** Invoice amount in cents, including VAT. */
  totalAmount?: number;
  /** Date on which the invoice was paid. */
  paidDate?: string;
  /** Date on which the invoice is supposed to be fully paid. */
  dueDate?: string;
  /** used for invoiceCloseDate */
  paidTimeStamp?: number;
  /** The amount still open to be paid. */
  dueAmount?: number;
  /** Can the outstanding amount be payed online. */
  allowPayment: boolean;
  /** Is a PDF specification available for download. */
  pdfAvailable: boolean;
  directDebit: InvoiceSummaryDirectDebit | null;
  paymentReference: string;
}

export interface InvoicePaymentMethod {
  type: InvoicePaymentMethodEnum;
  enabled: boolean;
}

export interface InvoicePayment {
  link: string;
  invoice?: InvoiceSummary;
  hash?: string;
  currency?: PayMeansCurrencyEnum;
  locale?: LocaleEnum;
  merchant?: string;
  acceptUrl?: string;
  declineUrl?: string;
  cancelUrl?: string;
  exceptionUrl?: string;
  backUrl?: string;
  homeUrl?: string;
}

export interface InvoicePaymentStatus {
  invoiceId: string;
  amount: number;
  paymentStatusCode: InvoicePaymentStatusEnum;
  detailedPaymentStatusCode: InvoicePaymentDetailStatusEnum;
}

export interface PaymentLink {
  link: string;
}
